import { useEffect, useState } from "react";
import { Download, ExternalLink, Monitor, Printer, QrCode, RefreshCw, Share2, Trophy } from "lucide-react";
import { toast } from "sonner";

import { ChalkActionButton, ChalkCard, SectionTitle } from "../chalk/primitives";
import { resultsPngUrl, resultsShareUrl, tournaments } from "../../lib/tournamentApi";
import BrandingPanel from "./BrandingPanel";
import ClaimsPanel from "./ClaimsPanel";
import { HubGate, Pill, pillBtn } from "./shared";

const VIEWS = [["bracket", "Brackets"], ["locations", "By location"], ["standings", "Pool standings"]];

export default function ShowcaseTab({ data, onChanged }) {
  const t = data.tournament;
  if (t.tier !== "hub") {
    return (
      <div data-testid="showcase-tab">
        <ResultsCard data={data} />
        <HubGate data={data} feature="Big Screen, posters, logos, sponsors and claim invites" onChanged={onChanged} />
      </div>
    );
  }
  return (
    <div data-testid="showcase-tab">
      <ResultsCard data={data} />
      <ScreenCard t={t} screen={data.screen} onChanged={onChanged} />
      <PrintCard data={data} />
      <BrandingPanel t={t} />
      <div style={{ height: 14 }} />
      <ClaimsPanel t={t} />
    </div>
  );
}

export async function shareResults(slug, division, tournamentName) {
  const url = resultsShareUrl(slug, division.id);
  try {
    const blob = await (await fetch(resultsPngUrl(slug, division.id, "portrait"))).blob();
    const file = new File([blob], `${division.name}-results.png`, { type: "image/png" });
    if (navigator.canShare?.({ files: [file] })) {
      await navigator.share({ files: [file], title: `${tournamentName} — ${division.name}`, text: `${tournamentName} results`, url });
      return;
    }
    if (navigator.share) { await navigator.share({ title: `${tournamentName} results`, url }); return; }
  } catch (e) { if (e?.name === "AbortError") return; }
  window.open(resultsPngUrl(slug, division.id, "portrait", true), "_blank");
}

function ResultsCard({ data }) {
  const t = data.tournament;
  const resultsUrl = `/t/${t.slug}/results`;
  return (
    <>
      <SectionTitle label="Results" />
      <ChalkCard data-testid="showcase-results" style={{ marginBottom: 14 }}>
        {data.divisions.map((d) => (
          <div key={d.id} data-testid={`showcase-result-${d.id}`} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0" }}>
            <Trophy size={15} color="var(--chalk-amber)" aria-hidden="true" />
            <div style={{ flex: 1, fontWeight: 600 }}>{d.name}</div>
            <Pill tone={d.status === "complete" ? "green" : "muted"}>{d.status === "complete" ? "Final" : "In progress"}</Pill>
            <a data-testid={`showcase-download-${d.id}`} aria-label="Download graphic" href={resultsPngUrl(t.slug, d.id, "portrait", true)}
               style={iconLink}><Download size={15} /></a>
            <button data-testid={`showcase-share-${d.id}`} aria-label="Share graphic" onClick={() => shareResults(t.slug, d, t.name)} style={iconLink}>
              <Share2 size={15} /></button>
          </div>
        ))}
        <a href={resultsUrl} target="_blank" rel="noreferrer" data-testid="showcase-open-results" style={linkBtn}>
          <ExternalLink size={14} aria-hidden="true" /> Open results page</a>
      </ChalkCard>
    </>
  );
}

function ScreenCard({ t, screen, onChanged }) {
  const [rotate, setRotate] = useState(screen?.rotate_sec || 20);
  const [views, setViews] = useState(screen?.views || VIEWS.map(([k]) => k));
  async function save() {
    try { await tournaments.setScreen(t.id, { rotate_sec: rotate, views }); toast.success("Big Screen saved."); await onChanged(); }
    catch (e) { toast.error(e.message || "Couldn't save."); }
  }
  const toggle = (k) => setViews(views.includes(k) ? views.filter((x) => x !== k) : [...views, k]);
  return (
    <>
      <SectionTitle label="Big Screen" />
      <ChalkCard data-testid="showcase-screen" style={{ marginBottom: 14 }}>
        <ScreenCode t={t} />
        <div style={{ fontSize: 12, color: "var(--chalk-muted)", marginBottom: 8 }}>Open this on a TV browser at the venue. It rotates views on its own at the interval below; scores and announcements refresh every 15 seconds.</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
          {VIEWS.map(([k, label]) => (
            <button key={k} data-testid={`screen-view-${k}`} onClick={() => toggle(k)} style={pillBtn(views.includes(k), "var(--green-mid)")}>{label}</button>
          ))}
        </div>
        <label style={{ fontSize: 13, display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
          Rotate every
          <select value={rotate} onChange={(e) => setRotate(Number(e.target.value))} data-testid="screen-rotate"
                  style={{ padding: 6, borderRadius: 6, border: "0.5px solid var(--chalk-border)" }}>
            {[10, 15, 20, 30, 45, 60].map((s) => <option key={s} value={s}>{`${s}s`}</option>)}
          </select>
        </label>
        <div style={{ display: "flex", gap: 8 }}>
          <ChalkActionButton label="Save" fullWidth={false} testId="screen-save" onClick={save} disabled={!views.length} />
          <a href={`/t/${t.slug}/screen`} target="_blank" rel="noreferrer" data-testid="screen-open" style={linkBtn}>
            <Monitor size={14} aria-hidden="true" /> Open Big Screen</a>
        </div>
      </ChalkCard>
    </>
  );
}

function ScreenCode({ t }) {
  const [code, setCode] = useState(null);
  useEffect(() => { tournaments.branding(t.id).then((b) => setCode(b.screen_code)).catch(() => {}); }, [t.id]);
  async function rotate() {
    try { setCode((await tournaments.newScreenCode(t.id)).screen_code); toast.success("New screen code made. The old code stops working."); }
    catch (e) { toast.error(e.message || "Couldn't make a new code."); }
  }
  if (!code) return null;
  return (
    <div data-testid="screen-pair" style={{ background: "var(--chalk-board, #1C2B1E)", color: "#F5F0E8", borderRadius: 10, padding: "12px 14px", marginBottom: 10 }}>
      <div style={{ fontSize: 12, opacity: 0.75 }}>On the TV, go to <b data-testid="screen-pair-url">{window.location.host}/screen</b> and enter</div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
        <div data-testid="screen-pair-code" style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 40, letterSpacing: "0.06em", lineHeight: 1.1 }}>{code}</div>
        <button data-testid="screen-pair-rotate" onClick={rotate} aria-label="Make a new code"
                style={{ background: "transparent", border: "0.5px solid rgba(245,240,232,0.4)", color: "#F5F0E8", borderRadius: 8, padding: "6px 10px",
                         fontSize: 12, fontWeight: 700, cursor: "pointer", display: "inline-flex", gap: 4, alignItems: "center" }}>
          <RefreshCw size={13} aria-hidden="true" /> New code</button>
      </div>
    </div>
  );
}

function PrintCard({ data }) {
  const t = data.tournament;
  const [size, setSize] = useState("letter");
  const [fill, setFill] = useState(true);
  const base = `/print/tournaments/${t.id}`;
  return (
    <>
      <SectionTitle label="Print" />
      <ChalkCard data-testid="showcase-print" style={{ marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
          {[["letter", "Letter / A4"], ["poster", "18×24 poster"]].map(([k, l]) => (
            <button key={k} data-testid={`print-size-${k}`} onClick={() => setSize(k)} style={pillBtn(size === k, "var(--green-mid)")}>{l}</button>
          ))}
          <button data-testid="print-fill-toggle" onClick={() => setFill(!fill)} style={pillBtn(fill, "var(--chalk-board)")}>{fill ? "Filled in" : "Blank"}</button>
        </div>
        {data.divisions.map((d) => (
          <a key={d.id} href={`${base}/bracket?division=${d.id}&size=${size}&fill=${fill ? 1 : 0}`} target="_blank" rel="noreferrer"
             data-testid={`print-bracket-${d.id}`} style={{ ...linkBtn, marginRight: 8, marginBottom: 6 }}>
            <Printer size={14} aria-hidden="true" /> {d.name} bracket</a>
        ))}
        <a href={`${base}/qr`} target="_blank" rel="noreferrer" data-testid="print-qr" style={linkBtn}>
          <QrCode size={14} aria-hidden="true" /> QR poster for check-in table</a>
      </ChalkCard>
    </>
  );
}

const iconLink = { background: "transparent", border: "none", color: "var(--green-mid)", cursor: "pointer", padding: 6, display: "inline-flex" };
const linkBtn = { display: "inline-flex", gap: 6, alignItems: "center", padding: "8px 12px", borderRadius: 8, fontSize: 12, fontWeight: 700,
                  border: "0.5px solid var(--chalk-border)", color: "var(--green-mid)", textDecoration: "none", marginTop: 6 };
